import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com';

const termsJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms & Conditions | CJ Solutions Ltd',
    url: `${siteUrl}/terms-and-conditions`,
    description: 'Read the Terms and Conditions of CJ Solutions Ltd governing the use of our compliance, AML, licensing, and financial advisory services in Cyprus.',
    publisher: {
      '@type': 'Organization',
      name: 'CJ Solutions Ltd',
      url: siteUrl,
    },
    inLanguage: 'en-GB',
    dateModified: '2025-01-01',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Terms & Conditions', item: `${siteUrl}/terms-and-conditions` },
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Terms & Conditions | CJ Solutions Ltd — Service Agreement Cyprus"
        description="Read the Terms and Conditions of CJ Solutions Ltd governing the use of our compliance, AML, licensing, and financial advisory services in Limassol, Cyprus."
        keywords="terms and conditions, service agreement, CJ Solutions terms, Cyprus consulting terms"
        canonical="/terms-and-conditions"
        jsonLd={termsJsonLd}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-slate-900 pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-block px-4 py-2 bg-amber-500/10 rounded-full mb-6">
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Terms & Conditions
          </h1>
          <p className="text-xl text-slate-300">
            Last updated: January 2025 · CJ Solutions Ltd.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">1. Introduction & Acceptance of Terms</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                Welcome to CJ Solutions Ltd. ("we," "us," "our," or "the Company"). These Terms and Conditions ("Terms") govern your access to and use of our services, including but not limited to iGaming & FX licensing, AML & KYC outsourcing, compliance outsourcing, tax & payroll outsourcing, accounting outsourcing, legal drafting, and company formation services (collectively, the "Services").
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                By engaging with our Services, you ("Client," "you," or "your") agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use our Services.
              </p>
              <p className="text-slate-700 leading-relaxed">
                These Terms constitute a legally binding agreement between you and CJ Solutions Ltd., a company registered in Cyprus.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Services Description */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">2. Services Description</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-6">
                CJ Solutions Ltd. provides professional business services tailored to meet the regulatory, compliance, and operational needs of our clients. Our Services include:
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span><strong>iGaming & FX Licensing:</strong> Assistance with obtaining and maintaining licenses for online gaming and foreign exchange operations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span><strong>AML & KYC Outsourcing:</strong> Anti-Money Laundering and Know Your Customer compliance services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span><strong>Compliance Outsourcing:</strong> Comprehensive regulatory compliance management and monitoring</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span><strong>Tax & Payroll Outsourcing:</strong> Tax planning, filing, and payroll administration services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span><strong>Accounting Outsourcing:</strong> Bookkeeping, financial reporting, and accounting services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span><strong>Legal Drafting:</strong> Preparation of contracts, agreements, and legal documentation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span><strong>Company Formation:</strong> Corporate structuring and entity establishment services</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-6">
                The scope, deliverables, and timelines for each Service will be defined in a separate Service Agreement or Statement of Work executed between the Company and the Client.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Eligibility & Client Obligations */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">3. Eligibility & Client Obligations</h2>
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">3.1 Eligibility</h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                To engage our Services, you must:
              </p>
              <ul className="space-y-3 text-slate-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Be at least 18 years of age or the age of legal majority in your jurisdiction</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Have the legal authority to enter into binding contracts</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Provide accurate, complete, and current information as requested</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">3.2 Client Obligations</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                As a Client, you agree to:
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Provide all necessary documentation, information, and cooperation required for the delivery of Services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Ensure that all information provided is accurate, truthful, and not misleading</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Comply with all applicable laws, regulations, and industry standards</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Pay all fees and charges in accordance with the agreed payment terms</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Not use our Services for any unlawful, fraudulent, or unauthorized purpose</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Fees & Payment Terms */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">4. Fees & Payment Terms</h2>
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">4.1 Service Fees</h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                Fees for our Services will be specified in the Service Agreement or Statement of Work. All fees are quoted in Euros (EUR) or US Dollars (USD) unless otherwise stated.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">4.2 Payment Terms</h3>
              <ul className="space-y-3 text-slate-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Payment is due within 14 days of invoice date unless otherwise agreed in writing</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>We reserve the right to require advance payment or deposits for certain Services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>Late payments may incur interest charges at a rate of 1.5% per month or the maximum rate permitted by law</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl mr-2 mt-1"></i>
                  <span>All fees are non-refundable unless expressly stated otherwise</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">4.3 Additional Costs</h3>
              <p className="text-slate-700 leading-relaxed">
                Clients are responsible for any third-party costs, government fees, regulatory charges, or other expenses incurred in connection with the Services, unless otherwise agreed in writing.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Confidentiality */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">5. Confidentiality</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                Both parties acknowledge that in the course of providing or receiving Services, they may have access to confidential information. "Confidential Information" includes, but is not limited to, business plans, financial data, client lists, proprietary processes, and any information marked as confidential.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                Each party agrees to:
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-shield-check-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Maintain the confidentiality of all Confidential Information</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Use Confidential Information solely for the purpose of performing or receiving Services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Not disclose Confidential Information to third parties without prior written consent</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Return or destroy Confidential Information upon termination of the relationship</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-6">
                This obligation does not apply to information that is publicly available, independently developed, or required to be disclosed by law or regulatory authority.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Intellectual Property */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">6. Intellectual Property</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                All intellectual property rights in materials, documents, templates, methodologies, and deliverables created by CJ Solutions Ltd. in the course of providing Services remain the property of the Company, unless expressly transferred in writing.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Upon full payment of all fees, the Client is granted a non-exclusive, non-transferable license to use deliverables solely for their internal business purposes.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Clients retain ownership of their pre-existing intellectual property and any data or information provided to us. We will not use Client intellectual property for any purpose other than delivering the agreed Services.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Limitation of Liability */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">7. Limitation of Liability & Disclaimers</h2>
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">7.1 Disclaimer of Warranties</h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                Our Services are provided on an "as is" and "as available" basis. While we strive to deliver high-quality professional services, we make no warranties, express or implied, regarding the accuracy, completeness, or suitability of our Services for any particular purpose.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">7.2 Limitation of Liability</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                To the maximum extent permitted by law, CJ Solutions Ltd. shall not be liable for:
              </p>
              <ul className="space-y-3 text-slate-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-error-warning-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Any indirect, incidental, special, consequential, or punitive damages</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-error-warning-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Loss of profits, revenue, data, or business opportunities</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-error-warning-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Damages arising from third-party actions, regulatory decisions, or force majeure events</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-error-warning-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Any damages exceeding the total fees paid by the Client in the 12 months preceding the claim</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">7.3 Professional Advice</h3>
              <p className="text-slate-700 leading-relaxed">
                Our Services do not constitute legal, financial, or tax advice unless explicitly stated. Clients are encouraged to seek independent professional advice where appropriate.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Termination */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">8. Termination</h2>
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">8.1 Termination by Client</h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                Clients may terminate Services by providing 30 days' written notice. The Client remains liable for all fees incurred up to the termination date, plus any reasonable costs associated with winding down the Services.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">8.2 Termination by Company</h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                We reserve the right to terminate Services immediately if:
              </p>
              <ul className="space-y-3 text-slate-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>The Client breaches these Terms or any Service Agreement</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Payment is overdue by more than 30 days</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>The Client engages in unlawful, fraudulent, or unethical conduct</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-amber-500 text-xl mr-3 mt-1"></i>
                  <span>Continuing the relationship would expose us to legal or reputational risk</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">8.3 Effect of Termination</h3>
              <p className="text-slate-700 leading-relaxed">
                Upon termination, all outstanding fees become immediately due and payable. Provisions relating to confidentiality, intellectual property, limitation of liability, and dispute resolution shall survive termination.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Governing Law */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">9. Governing Law & Jurisdiction</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                These Terms and any disputes arising out of or related to these Terms or the Services shall be governed by and construed in accordance with the laws of the Republic of Cyprus, without regard to its conflict of law provisions.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Any legal action or proceeding arising under these Terms shall be brought exclusively in the courts of Limassol, Cyprus, and the parties hereby consent to the personal jurisdiction and venue of such courts.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Where permitted by law, the parties agree to attempt to resolve disputes through good-faith negotiation before initiating formal legal proceedings.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Amendments */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">10. Amendments</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting the updated Terms on our website with a revised "Last updated" date.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Continued use of our Services after any such changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Material changes that significantly affect your rights or obligations will be communicated to active Clients via email or other appropriate means.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 my-12"></div>

          {/* Contact Information */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">11. Contact Information</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-8">
                If you have any questions, concerns, or requests regarding these Terms & Conditions, please contact us:
              </p>
              
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">CJ Solutions Ltd.</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-amber-500 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 mb-1">Email</p>
                      <a href="mailto:info@cjsolutionsltd.com" className="text-lg text-slate-700 hover:text-amber-500 transition-colors">
                        info@cjsolutionsltd.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-amber-500 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 mb-1">Phone</p>
                      <a href="tel:+35795981879" className="text-lg text-slate-700 hover:text-amber-500 transition-colors">
                        +357 95981879
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-amber-500 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 mb-1">Address</p>
                      <p className="text-lg text-slate-700">
                        Limassol, Cyprus
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Important Notice</h3>
                <p className="text-slate-700 leading-relaxed">
                  By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions. If you do not agree to these Terms, please do not engage with our Services.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}